'use client';

import { ReactNode, useEffect, useMemo } from 'react';

import { useAuiState } from '@assistant-ui/react';

import { useResultsHeldForGuest } from '@/components/assistant-ui/hitl/use-hitl-call';
import { getToolStatusFlags, ToolStatus } from '@/components/assistant-ui/tool-status';
import { ToolCallInfo, useRenderMode } from '@/components/assistant-ui/use-tool-group-state';
import { useReadOnly } from '@/components/providers/read-only-provider';
import { useAnalytics } from '@/hooks/use-analytics';
import { useSettlementToken } from '@/hooks/use-content-settlement';

export const CAROUSEL_ENTER_ANIMATION = 'animate-in duration-300 fade-in slide-in-from-bottom-1';

const MAX_TRACKED_ENTRIES = 50;

function boundedAdd({ set, value }: { set: Set<string>; value: string }) {
  set.add(value);
  if (set.size > MAX_TRACKED_ENTRIES) {
    set.delete(set.values().next().value!);
  }
}

/**
 * Tracks whether the current message was ever seen during streaming.
 * Uses a module-level Set so the flag survives component remounts
 * (e.g. when the component tree is recreated on status change).
 */
const streamedMessageIds = new Set<string>();

/**
 * Prevents duplicate carousel_shown events across component remounts.
 * Keyed by `messageId:toolName` so each tool type fires once per message.
 */
const trackedCarousels = new Set<string>();

export function useMountedDuringStreaming(): boolean {
  const messageId = useAuiState(({ message }) => message?.id);
  const isRunning = useAuiState(({ message }) => message?.status?.type === 'running');

  if (isRunning && messageId) {
    boundedAdd({ set: streamedMessageIds, value: messageId });
  }

  // In read-only mode messages are never streamed, so skip the animation.
  const isReadOnly = useReadOnly();
  if (isReadOnly) return false;

  return !!messageId && streamedMessageIds.has(messageId);
}

export interface CarouselLoadingFlags {
  isLoading: boolean;
  showLoadingAbove: boolean;
  showContent: boolean;
}

interface GetCarouselLoadingFlagsParams {
  status: ToolStatus;
  itemCount: number;
  isLoadingOverride?: boolean;
  alwaysShowContent?: boolean;
}

export function getCarouselLoadingFlags({
  status,
  itemCount,
  isLoadingOverride,
  alwaysShowContent = false,
}: GetCarouselLoadingFlagsParams): CarouselLoadingFlags {
  const { isRunning, isError } = getToolStatusFlags(status);
  const isLoading = isLoadingOverride ?? isRunning;

  // Show loading spinner above the carousel while the tool group is still
  // running — even if some partial results exist. This prevents flashing
  // partial content that reshuffles when remaining results arrive.
  const showLoadingAbove = isRunning && isLoading;

  return {
    isLoading,
    showLoadingAbove,
    // The empty state lives inside the content, so withholding it on failure is also what
    // stops a transport failure from reading as "no results found".
    showContent:
      !isError && (showLoadingAbove ? false : alwaysShowContent || itemCount > 0 || !isLoading),
  };
}

export interface GroupedToolCarouselProps {
  shouldRender: boolean;
  resultsReady: boolean;
  groupToolCalls: ToolCallInfo[];
  loadingFlags: CarouselLoadingFlags;
  children: ReactNode;
  /** When false, skips the entrance animation (e.g. for history messages). */
  animate?: boolean;
  /** Pre-computed item identifiers for carousel_shown analytics. */
  itemIdentifiers?: string[];
}

function useTrackCarouselShown({
  showCarousel,
  animate,
  itemIdentifiers,
  toolName,
}: {
  showCarousel: boolean;
  animate?: boolean;
  itemIdentifiers?: string[];
  toolName: string;
}) {
  const { trackCarouselShown } = useAnalytics();
  const messageId = useAuiState(({ message }) => message?.id);

  useEffect(() => {
    if (!showCarousel || !animate || !messageId) return;
    if (!itemIdentifiers || itemIdentifiers.length === 0) return;
    const key = `${messageId}:${toolName}`;
    if (trackedCarousels.has(key)) return;
    boundedAdd({ set: trackedCarousels, value: key });
    trackCarouselShown({ products: itemIdentifiers, toolName });
  }, [showCarousel, animate, itemIdentifiers, toolName, trackCarouselShown, messageId]);
}

export function GroupedToolCarousel({
  shouldRender,
  resultsReady,
  groupToolCalls,
  loadingFlags,
  children,
  animate,
  itemIdentifiers,
}: GroupedToolCarouselProps): ReactNode {
  const renderMode = useRenderMode();
  const { isLoading, showContent } = loadingFlags;

  // Signal to the settlement system that this tool group is still loading.
  // Resolves automatically when isLoading becomes false or on unmount.
  useSettlementToken(isLoading);

  // In 'carousel' render mode (DeferredCarousels at message bottom),
  // show when results aren't ready inline (i.e. deferred).
  // In inline mode, show when results ARE ready (preceding text exists).
  const resultsHeld = useResultsHeldForGuest();
  const showCarousel =
    !resultsHeld && (renderMode === 'carousel' ? !resultsReady : resultsReady) && showContent;

  useTrackCarouselShown({
    showCarousel,
    animate,
    itemIdentifiers,
    toolName: groupToolCalls[0]?.toolName ?? 'unknown',
  });

  if (!shouldRender || !showCarousel) return null;

  return <div className={animate !== false ? CAROUSEL_ENTER_ANIMATION : undefined}>{children}</div>;
}

interface UseCarouselLoadingFlagsParams {
  status: ToolStatus;
  itemCount: number;
  alwaysShowContent?: boolean;
  isExtraLoading?: boolean;
  /** When true, the AI has moved on to generating text after this group — stop suppressing the empty state. */
  hasTextAfterGroup?: boolean;
}

export function useCarouselLoadingFlags({
  status,
  itemCount,
  alwaysShowContent,
  isExtraLoading = false,
  hasTextAfterGroup = false,
}: UseCarouselLoadingFlagsParams): CarouselLoadingFlags {
  const isMessageRunning = useAuiState(({ message }) => message?.status?.type === 'running');

  return useMemo(() => {
    // Suppress "no results" while streaming — more results may still arrive.
    // But if text appeared after the group, the AI has moved on and the
    // result is final, so allow the empty state to render.
    const noResultsWhileMessageRunning = isMessageRunning && itemCount === 0 && !hasTextAfterGroup;
    const extraWhileRunning = isMessageRunning && isExtraLoading;
    const shouldOverrideLoading =
      (noResultsWhileMessageRunning || extraWhileRunning) && status.type === 'complete';
    const isLoadingOverride = shouldOverrideLoading || undefined;
    return getCarouselLoadingFlags({ status, itemCount, isLoadingOverride, alwaysShowContent });
  }, [status, itemCount, isMessageRunning, isExtraLoading, alwaysShowContent, hasTextAfterGroup]);
}
